/**
 * Route Screens Configuration
 *
 * Lazy-loaded screen components mapped to route codes.
 * Uses React.lazy() for code splitting and performance optimization.
 *
 * Structure:
 * - Grouped by feature module (DASHBOARD_SCREEN, SUPPORT_PLAN_SCREENS, etc.)
 * - Each entry maps a ROUTE_CODE to a lazy-loaded component
 * - Combined into ROUTE_SCREENS export for navigation usage
 *
 * Usage:
 * - Components are loaded on-demand when routes are accessed
 *
 * Note:
 * - Paths should match the route configuration
 * - Always keep component imports updated with file locations
 */

import { lazy } from "react";
import { ROUTE_CODES } from "./route-codes";

const DASHBOARD_SCREEN = {
  [ROUTE_CODES.DASHBOARD]: lazy(() => import("../views/Dashboard")),
};

const SUPPORT_PLAN_SCREENS = {
  [ROUTE_CODES.SUPPORT_PLAN]: lazy(() => import("../views/Home")),
  [ROUTE_CODES.CLIENT_SUPPORT_PLAN]: lazy(() => import("../views/SupportPlan")),
  [ROUTE_CODES.ASSIGNED_CLIENT]: lazy(
    () => import("../components/carePlanAccordians/AssignedClientPanel"),
  ),
  [ROUTE_CODES.SUPPORT_CIRCLE]: lazy(
    () => import("../components/carePlanAccordians/CareCircle"),
  ),
  [ROUTE_CODES.CS_REPRESENTATIVE]: lazy(
    () => import("../components/carePlanAccordians/CSRepresentative"),
  ),
  [ROUTE_CODES.EMERGENCY_PLANNING]: lazy(
    () => import("../components/carePlanAccordians/EmergencyPlanning"),
  ),
  [ROUTE_CODES.PERSONAL_SUPPORT_DETAILS]: lazy(
    () => import("../components/carePlanAccordians/PersonalSupportDetails"),
  ),
  [ROUTE_CODES.CARE_PLAN_ACTIONS]: lazy(
    () => import("../components/carePlanAccordians/CarePlanActions"),
  ),
  [ROUTE_CODES.CARE_PLAN_CALENDER]: lazy(
    () =>
      import(
        "../components/carePlanAccordians/calendar/CarePlanActionsCalender"
      ),
  ),
  [ROUTE_CODES.CALENDER_HEAT_MAP]: lazy(
    () => import("../components/carePlanAccordians/CalendarHeatMap"),
  ),
  [ROUTE_CODES.TECHNOLOGY_DETAILS]: lazy(
    () => import("../components/carePlanAccordians/TechnologyDetails"),
  ),
  [ROUTE_CODES.ALERT_CONDITIONS]: lazy(
    () =>
      import(
        "@carePlanAccordians/alertConditions/AlertConditions"
      ),
  ),
  [ROUTE_CODES.HEALTH_INFORMATION]: lazy(
    () => import("../components/carePlanAccordians/HealthInformation"),
  ),
  [ROUTE_CODES.GENERATE_DOCUMENT]: lazy(
    () => import("../components/carePlanAccordians/GenerateDocument"),
  ),
  [ROUTE_CODES.SUSPENSION_HISTORY]: lazy(
    () => import("../components/carePlanAccordians/SuspensionHistory"),
  ),
  [ROUTE_CODES.SUPPORT_PLAN_ACKNOWLEDGEMENT_HISTORY]: lazy(
    () =>
      import(
        "../components/carePlanAccordians/SupportPlanAcknowledgementHistory"
      ),
  ),
  [ROUTE_CODES.SUPPORT_PLAN_INDIVIDUAL_VERSION_HISTORY]: lazy(
    () => import("../components/carePlanAccordians/IndividualVersionHistory"),
  ),
  [ROUTE_CODES.SATISFACTION_SCORES]: lazy(
    () => import("../components/carePlanAccordians/SatisfactionScores"),
  ),
  [ROUTE_CODES.INDIVIDUAL_COMMUNICATIONS_REPORT]: lazy(
    () => import("../components/carePlanAccordians/CommunicationsReport"),
  ),
  [ROUTE_CODES.BILLING_HISTORY]: lazy(
    () => import("../components/carePlanAccordians/BillingHistory"),
  ),
  [ROUTE_CODES.MUI_INCIDENT_HISTORY]: lazy(
    () => import("../components/carePlanAccordians/MuiIncidentHistory"),
  ),
  [ROUTE_CODES.OBJECTIVE_CHECKLIST_REPORT]: lazy(
    () => import("../components/carePlanAccordians/ObjectiveChecklistReport"),
  ),
  [ROUTE_CODES.SUPPORT_NOTE_HISTORY_AUDIT]: lazy(
    () => import("../components/carePlanAccordians/SupportNotesHistoryAudit"),
  ),
  [ROUTE_CODES.SUPPORT_PLAN_VIEWERS]: lazy(
    () => import("../components/carePlanAccordians/SupportPlanViewers"),
  ),
};

const SUSPENSION_MANAGER_SCREENS = {
  [ROUTE_CODES.SUSPENSION_MANAGER]: lazy(
    () => import("../views/SuspensionManager"),
  ),
};

const FS_TOOLS_SCREENS = {
  [ROUTE_CODES.FS_TOOLS]: lazy(() => import("../views/FSTools")),
  [ROUTE_CODES.GEOCOMM_MAINTENANCE]: lazy(
    () => import("../components/fsTools/GeoCommPendantPing"),
  ),
  [ROUTE_CODES.PROVISION_GEOCOMM_DEVICE]: lazy(
    () => import("../components/fsTools/ProvisionWristband"),
  ),
  [ROUTE_CODES.WRISTBAND_CONTACTS]: lazy(
    () => import("../components/fsTools/WristbandContacts"),
  ),
  [ROUTE_CODES.WRISTBAND_GEOFENCE]: lazy(
    () => import("../components/fsTools/WristbandGeofence"),
  ),
  [ROUTE_CODES.GENERATE_SHORT_TOKEN]: lazy(
    () => import("../components/fsTools/ShortTokenGenerator"),
  ),
  [ROUTE_CODES.REBOOT_WRISTBAND]: lazy(
    () => import("../components/fsTools/WristbandReboot"),
  ),
  [ROUTE_CODES.SHINOBI_CAMERA_CONFIG]: lazy(
    () => import("../components/fsTools/ShinobiCameraConfiguration"),
  ),
  [ROUTE_CODES.SUPPORT_CENTER_TABS]: lazy(
    () => import("../components/fsTools/SupportCenterTabs"),
  ),
  [ROUTE_CODES.FOSCAM_CAMERA_CONFIG]: lazy(
    () => import("../components/fsTools/FoscamCameraConfiguration"),
  ),
  [ROUTE_CODES.PROVISION_IGUARD_DEVICE]: lazy(
    () => import("../components/fsTools/ProvisionIGuard"),
  ),
  [ROUTE_CODES.PROVISION_SENSOR_SYSTEM]: lazy(
    () => import("../components/fsTools/ProvisionSensorSystem"),
  ),
  [ROUTE_CODES.SENSOR_SYSTEM_MESSAGE_STATUS]: lazy(
    () => import("../components/fsTools/SensorSystemMessageStatus"),
  ),
  [ROUTE_CODES.SENSOR_OUTAGE_STATUS]: lazy(
    () => import("../components/fsTools/SensorSystemOutageStatus"),
  ),
  [ROUTE_CODES.IUS_SENSOR_STATUS]: lazy(
    () => import("../components/fsTools/IUSSensorStatus"),
  ),
  [ROUTE_CODES.BATTERY_LEVEL]: lazy(
    () => import("../components/fsTools/TabletDashboard"),
  ),
  [ROUTE_CODES.DEVICE_GRID]: lazy(
    () => import("../components/fsTools/DeviceGridDashboard"),
  ),
  [ROUTE_CODES.TABLET_MAINTENANCE]: lazy(
    () => import("../components/fsTools/TabletMaintenance"),
  ),
  [ROUTE_CODES.TABLET_USERS]: lazy(
    () => import("../components/fsTools/TabletUsers"),
  ),
};

const BILLING_TOOLS_SCREENS = {
  [ROUTE_CODES.BILLING_TOOLS]: lazy(() => import("../views/BillingTools")),
  [ROUTE_CODES.UPLOAD_WEEKLY_EMBS_FILE]: lazy(
    () => import("../components/billingTools/EMBSWeeklyFile"),
  ),
  [ROUTE_CODES.GENERATE_EMBS_SUBMISSION_FILE]: lazy(
    () => import("../components/billingTools/GenerateEMBSSubmissionFile"),
  ),
  [ROUTE_CODES.EDIT_BILLING_PARAMETERS]: lazy(
    () => import("../components/billingTools/EMBSUserBillingParameters"),
  ),
  [ROUTE_CODES.BILLING_WEEK_SUMMARY]: lazy(
    () => import("../components/billingTools/BillingWeekSummary"),
  ),
  [ROUTE_CODES.CREATE_EMBS_HOURS_FILE]: lazy(
    () => import("../components/billingTools/CreateEMBSHoursFile"),
  ),
  [ROUTE_CODES.LIST_IMPORTED_FILES]: lazy(
    () => import("../components/billingTools/ListImportedFiles"),
  ),
};

const SENSOR_MANAGER_SCREENS = {
  [ROUTE_CODES.SENSOR_MANAGER]: lazy(() => import("../views/SensorManager")),
};

const APP_SETTINGS_SCREENS = {
  [ROUTE_CODES.SETTINGS]: lazy(() => import("../views/Settings")),
  [ROUTE_CODES.SPECIAL_HANDLING_MANAGER]: lazy(
    () =>
      import(
        "../components/settings/SpecialHandlingManager/SpecialHandlingBanners"
      ),
  ),
  [ROUTE_CODES.GLOBAL_ANNOUNCEMENTS]: lazy(
    () =>
      import("../components/settings/GlobalAnnouncements/GlobalAnnouncements"),
  ),
  [ROUTE_CODES.COMMUNICATION_METHODS]: lazy(
    () =>
      import(
        "../components/settings/CommunicationMethods/CommunicationMethods"
      ),
  ),
  [ROUTE_CODES.USER_ADMINISTRATION_MAINTENANCE]: lazy(
    () => import("../components/settings/UserAdmin/UserMaintenance"),
  ),
  [ROUTE_CODES.SSO_ASSOCIATION_MAINTENANCE]: lazy(
    () => import("../components/settings/UserAdmin/SSOAssociationMaintenance"),
  ),
  [ROUTE_CODES.USER_RIGHTS_MAINTENANCE]: lazy(
    () => import("../components/settings/UserAdmin/RightMaintenance"),
  ),
  [ROUTE_CODES.USER_ROLES_MAINTENANCE]: lazy(
    () => import("../components/settings/UserAdmin/RoleMaintenance"),
  ),
  [ROUTE_CODES.SYSTEM_TAG_MAINTENANCE]: lazy(
    () => import("../components/settings/UserAdmin/SystemTagMaintenance"),
  ),
  [ROUTE_CODES.TABLET_NOTIFICATION_CONFIGURATION]: lazy(
    () =>
      import(
        "../components/settings/UserAdmin/TabletNotificationConfiguration"
      ),
  ),
  [ROUTE_CODES.SUPPORT_PLAN_LOGS]: lazy(
    () =>
      import(
        "../components/settings/ApplicationLogs/ClientSupportPlanLogs/SupportPlanClientLogs"
      ),
  ),
  [ROUTE_CODES.ADMIN_USER_SUPPORT_PLAN_LOGS]: lazy(
    () =>
      import(
        "../components/settings/ApplicationLogs/AdminSupportPlanLogs/AdminUserSupportPlanLogs"
      ),
  ),
  [ROUTE_CODES.QOL_MAINTENANCE]: lazy(
    () => import("../components/settings/QoLMaintenance/QolMaintenance"),
  ),
  [ROUTE_CODES.ANSWER_MAINTENANCE]: lazy(
    () => import("../components/settings/QoLMaintenance/AnswerMaintenance"),
  ),
  [ROUTE_CODES.OBJECTIVES_MAINTENANCE]: lazy(
    () =>
      import(
        "../components/settings/ObjectiveMaintenance/ObjectiveMaintenance"
      ),
  ),
  [ROUTE_CODES.CHECKLIST_MAINTENANCE]: lazy(
    () =>
      import(
        "../components/settings/ChecklistMaintenance/ChecklistMaintenance"
      ),
  ),
  [ROUTE_CODES.UNVERIFIED_NUMBER_MAINTENANCE]: lazy(
    () =>
      import("../components/settings/Five9Administration/UnverifiedNumbers"),
  ),
  [ROUTE_CODES.DASHBOARD_PAGE_MAINTENANCE]: lazy(
    () =>
      import(
        "../components/settings/DashboardAdmin/DashboardPageMaintenance/DashboardPageMaintenance"
      ),
  ),
  [ROUTE_CODES.DASHBOARD_FUNCTION_MAINTENANCE]: lazy(
    () =>
      import(
        "../components/settings/DashboardAdmin/DashboardFunctionMaintenance/DashboardFunctionMaintenance"
      ),
  ),
  [ROUTE_CODES.DASHBOARD_FUNCTION_PARAMS_OPTIONS]: lazy(
    () =>
      import(
        "../components/settings/DashboardAdmin/DashboardFunctionParamsOptions/DashboardFunctionParamsOptions"
      ),
  ),
  [ROUTE_CODES.DASHBOARD_OBJECT_TYPES]: lazy(
    () =>
      import(
        "../components/settings/DashboardAdmin/DashboardObjectTypes/DashboardObjectTypes"
      ),
  ),
  [ROUTE_CODES.DASHBOARD_PAGE_OBJECTS]: lazy(
    () =>
      import(
        "../components/settings/DashboardAdmin/DashboardPageObjects/DashboardPageObjects"
      ),
  ),
  [ROUTE_CODES.DASHBOARD_PAGE_BUILDER]: lazy(
    () =>
      import(
        "../components/settings/DashboardAdmin/DashboardPageBuilder/DashboardPageBuilder"
      ),
  ),
  [ROUTE_CODES.SENTIMENT_MAINTENANCE]: lazy(
    () =>
      import("../components/settings/SentimentScoring/SentimentMaintenance"),
  ),
  [ROUTE_CODES.MUI_REPORT_DATA_TYPES]: lazy(
    () => import("../components/settings/MuiConfig/MuiDataType"),
  ),
  [ROUTE_CODES.MUI_REPORT_FIELDS]: lazy(
    () => import("../components/settings/MuiConfig/MuiDataField"),
  ),
  [ROUTE_CODES.MUI_REPORT_TAB_CONFIG]: lazy(
    () => import("../components/settings/MuiConfig/MuiTabConfig"),
  ),
  [ROUTE_CODES.MUI_REPORT_PROFILES]: lazy(
    () => import("../components/settings/MuiConfig/ProfileList"),
  ),
  [ROUTE_CODES.MUI_REPORT_TAGS]: lazy(
    () => import("../components/settings/MuiConfig/MuiTag"),
  ),
  [ROUTE_CODES.SUPPORT_PLAN_TEMPLATE_MAINTENANCE]: lazy(
    () => import("../components/settings/SupportPlanTemplateAdministration/TemplateMaintenance/TemplateMaintenance"),
  ),
};

const AUTH_FROM_TOKEN_SCREEN = {
  [ROUTE_CODES.AUTH_FROM_TOKEN]: lazy(() => import("../views/AuthFromToken")),
};

const CALENDAR_VIEWER_SCREEN = {
  [ROUTE_CODES.CALENDAR_VIEWER]: lazy(() => import("../views/CalendarViewer")),
};

const IUS_SENSOR_ROUTES = {
  [ROUTE_CODES.IUS_SENSOR]: lazy(() => import("../views/IUSSensorPop")),
};

export const ROUTE_SCREENS = {
  ...DASHBOARD_SCREEN,
  ...SUPPORT_PLAN_SCREENS,
  ...SUSPENSION_MANAGER_SCREENS,
  ...FS_TOOLS_SCREENS,
  ...BILLING_TOOLS_SCREENS,
  ...SENSOR_MANAGER_SCREENS,
  ...APP_SETTINGS_SCREENS,
  ...AUTH_FROM_TOKEN_SCREEN,
  ...CALENDAR_VIEWER_SCREEN,
  ...IUS_SENSOR_ROUTES,
};
